import { applyPaddingCSS } from '@/utils/generatePadding';
import { graphql } from 'gatsby';
import Logo from './components/Logo';
import { StyledSection, Container, Title, LogoContainer } from './LogoBlock.styles';
import type { BlockHTMLId, BlockHTMLClassName } from '@/utils/renderBlock';
import { useEffect, useState } from 'react';

const LogoBlock = ({
  logoBlockElements: {
    headline,
    headline_second_part,
    highlighted_text,
    logos,
    mode,
    options,
    ...props
  },
  system,
  id,
  className,
}: Queries.LOGO_BLOCKFragment & BlockHTMLId & BlockHTMLClassName) => {
  const MAX_VISIBLE_LOGOS = 8;
  const MAX_VISIBLE_LOGOS_CONDENSED = 6;
  const LOGO_TRANSITION_DELAY = 2000;
  const isCondensed = mode?.value === 'condensed';
  const maxVisibleLogos = isCondensed ? MAX_VISIBLE_LOGOS_CONDENSED : MAX_VISIBLE_LOGOS;
  const isAnimated = logos.value.length >= maxVisibleLogos * 2;
  const hasHighlightedText =
    typeof highlighted_text?.value === 'string' && highlighted_text.value.length > 0;
  const hasColoredBackground = options?.value?.includes('colored_background') ?? false;
  const [flippedLogos, setFlippedLogos] = useState<boolean[]>(Array(maxVisibleLogos).fill(false));

  const logosToDisplay = isAnimated
    ? Array.from({ length: maxVisibleLogos }, (_, i) => (
        <Logo
          key={logos.value[i].id}
          logo={logos.value[i]}
          altLogo={logos.value[i + maxVisibleLogos]}
          isFlipped={flippedLogos[i]}
        />
      ))
    : logos.value.slice(0, maxVisibleLogos).map((logo) => <Logo key={logo.id} logo={logo} />);

  useEffect(() => {
    if (!isAnimated) {
      return;
    }
    const flipPattern = [7, 2, 5, 0, 3, 6, 1, 4];
    const condensedFlipPattern = [4, 1, 3, 0, 2, 5];
    setFlippedLogos(Array(maxVisibleLogos).fill(false));
    let flipFrame = 0;

    const flip = () => {
      const indexToFlip = (isCondensed ? condensedFlipPattern : flipPattern)[flipFrame];
      setFlippedLogos((prevFlipped) => prevFlipped.with(indexToFlip, !prevFlipped[indexToFlip]));
      flipFrame = (flipFrame + 1) % maxVisibleLogos;
    };

    const id = setInterval(flip, LOGO_TRANSITION_DELAY);
    return () => {
      clearInterval(id);
    };
  }, [isAnimated, isCondensed, maxVisibleLogos]);

  return (
    <StyledSection
      css={applyPaddingCSS(props)}
      data-codename={system.codename}
      hasColoredBackground={hasColoredBackground}
      id={id}
      className={className}
    >
      <Container isCondensed={isCondensed}>
        <Title>
          {headline.value}
          {hasHighlightedText && <span> {highlighted_text.value} </span>}
          {headline_second_part?.value}
        </Title>
        <LogoContainer>{logosToDisplay}</LogoContainer>
      </Container>
    </StyledSection>
  );
};

export const fragment = graphql`
  fragment LOGO_BLOCK on kontent_item_logo_block {
    __typename
    system {
      codename
    }
    logoBlockElements: value {
      ...COMPONENT_A_B_TEST
      ...PADDING
      headline {
        value
      }
      headline_second_part {
        value
      }
      highlighted_text {
        value
      }
      logos {
        value {
          ...LOGO
        }
      }
      mode {
        value
      }
      options {
        value
      }
    }
  }
`;

export default LogoBlock;
